
import React, { useState } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Smartphone,
  Signal,
  WifiOff,
  MapPin,
  Clock,
  XCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import "../Style/DeviceCondition.css";

const API_BASE_URL = "https://api.yellowmatrix.co.in";

// --- Reusable Components ---
const StatCard = ({ item }) => (
  <div className={`stat-card-DeviceCondition ${item.colorClass}`}>
    <div className="stat-card-icon-wrapper-DeviceCondition">{item.icon}</div>
    <div>
      <p className="stat-card-name-DeviceCondition">{item.name}</p>
      <p className="stat-card-value-DeviceCondition">{item.value}</p>
    </div>
  </div>
);

const TrackingInfoCard = ({ item }) => (
  <div className="tracking-info-card-DeviceCondition">
    <div className="tracking-info-icon-wrapper-DeviceCondition">
      {item.icon}
    </div>
    <p className="tracking-info-name-DeviceCondition">{item.name}</p>
    <p className="tracking-info-value-DeviceCondition">
      {item.value}{" "}
      <span className="tracking-info-unit-DeviceCondition">{item.unit}</span>
    </p>
  </div>
);

// -------- API FUNCTIONS --------
const fetchTrackingSummary = async () => {
  const token = localStorage.getItem("token");
  const res = await axios.get(`${API_BASE_URL}/dashboard/tracking-summary`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

const fetchDevicesByCustomer = async () => {
  const token = localStorage.getItem("token");
  const res = await axios.get(`${API_BASE_URL}/dashboard/devices-by-customer`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

// NEW: Fetch Assigned Vehicles with Pagination
const fetchAssignedVehicles = async ({ queryKey }) => {
  // eslint-disable-next-line
  const [_key, page, perPage] = queryKey;
  const token = localStorage.getItem("token");
  const res = await axios.get(
    `${API_BASE_URL}/vehiclereg/assigned?page=${page}&per_page=${perPage}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  return res.data;
};

// --- Main App Component ---
export default function DeviceCondition() {
  // Pagination State
  const [page, setPage] = useState(1);
  const [perPage] = useState(10); // Adjust items per page if needed

  const [deviceStatusData] = useState([
    {
      name: "Tracking",
      value: 80,
      colorClass: "tracking-card-DeviceCondition",
      icon: <Smartphone size={24} />,
    },
    {
      name: "Offline",
      value: 12,
      colorClass: "offline-card-DeviceCondition",
      icon: <WifiOff size={24} />,
    },
    {
      name: "No Data",
      value: 5,
      colorClass: "no-data-card-DeviceCondition",
      icon: <XCircle size={24} />,
    },
    {
      name: "Disconnected",
      value: 3,
      colorClass: "disconnected-card-DeviceCondition",
      icon: <Signal size={24} />,
    },
  ]);

  // -------- TanStack Queries --------
  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ["tracking-summary"],
    queryFn: fetchTrackingSummary,
    retry: 1,
  });

  const { data: devicesData, isLoading: devicesLoading } = useQuery({
    queryKey: ["devices-by-customer"],
    queryFn: fetchDevicesByCustomer,
    retry: 1,
  });

  // NEW: Query for Table Data
  const {
    data: vehicleDataResponse,
    isLoading: vehiclesLoading,
    isFetching: vehiclesFetching,
  } = useQuery({
    queryKey: ["assigned-vehicles", page, perPage],
    queryFn: fetchAssignedVehicles,
    keepPreviousData: true, // Keeps table from flashing blank while loading next page
    retry: 1,
  });

  const loading = summaryLoading || devicesLoading;

  // Extract table and pagination info safely
  const tableData = vehicleDataResponse?.Vehicle || [];
  const paginationInfo = vehicleDataResponse?.Pagination || {
    Page: 1,
    TotalPages: 1,
    HasNext: false,
    HasPrev: false,
  };

  // -------- Safe fallback values --------
  const trackingData = [
    {
      name: "Total Distance Tracked",
      value: `${summaryData?.total_distance} km` ?? "0km",
      unit: "",
      icon: <MapPin size={28} />,
    },
    {
      name: "Total Time Tracked",
      value: summaryData
        ? `${summaryData.total_time.days ?? 0} days ${summaryData.total_time.hours ?? 0} hours ${summaryData.total_time.minutes ?? 0} minutes`
        : "0 hr",
      unit: "",
      icon: <Clock size={28} />,
    },
  ];

  const chartData = devicesData?.length
    ? devicesData
    : [{ name: "No Data", "Number of Devices": 0 }];

  if (loading) {
    return (
      <div
        className="loading-container"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontWeight: 600,
        }}
      >
        <p style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Loader2 className="animate-spin" size={24} /> Loading
        </p>
      </div>
    );
  }

  return (
    <div className="page-container-DeviceCondition">
      <div className="container-DeviceCondition">
        <header className="header-DeviceCondition">
          <h1 className="header-title-DeviceCondition">Device Dashboard</h1>
          <p className="header-subtitle-DeviceCondition">
            Overview of device status and tracking information.
          </p>
          <hr className="line-DeviceCondition" width="100%" />
        </header>

        <div className="status-grid-DeviceCondition">
          {deviceStatusData.map((item) => (
            <StatCard key={item.name} item={item} />
          ))}
        </div>

        <main className="main-content-grid-DeviceCondition">
          <section className="chart-section-DeviceCondition">
            <div className="device-chart-container-DeviceCondition">
              <h3 className="device-chart-title-DeviceCondition">
                Devices by Customer
              </h3>

              <div
                className="device-chart-wrapper-DeviceCondition"
                style={{ overflowX: "auto" }}
              >
                <div
                  style={{
                    minWidth: `${Math.max(chartData.length * 120, 700)}px`,
                    height: "350px",
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />

                      <XAxis
                        dataKey="name"
                        interval={0}
                        tick={{ fontSize: 12 }}
                      />

                      <YAxis />

                      <Tooltip cursor={{ fill: "rgba(239, 246, 255, 0.5)" }} />

                      <Legend />

                      <Bar
                        dataKey="Number of Devices"
                        fill="#3B82F6"
                        barSize={50}
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </section>

          <aside className="aside-DeviceCondition">
            {trackingData.map((item) => (
              <TrackingInfoCard key={item.name} item={item} />
            ))}
          </aside>
        </main>

        {/* 🔥 DYNAMIC TABLE SECTION WITH PAGINATION */}
        <section className="table-section-DeviceCondition">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h3 className="table-title-DeviceCondition">
              Device Details Table
            </h3>
            {vehiclesFetching && (
              <Loader2
                className="animate-spin"
                size={18}
                style={{ color: "#3B82F6" }}
              />
            )}
          </div>

          <div className="table-wrapper-DeviceCondition">
            <table
              className="device-table-DeviceCondition"
              style={{ width: "100%", textAlign: "left" }}
            >
              <thead>
                <tr>
                  <th>Device ID</th>
                  <th>Vehicle Number</th>
                  <th>IMEI</th>
                  <th>VIN</th>
                  <th>ICCID</th>
                  <th>Certificate</th>
                </tr>
              </thead>

              <tbody>
                {vehiclesLoading ? (
                  <tr>
                    <td
                      colSpan="6"
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      Loading vehicles...
                    </td>
                  </tr>
                ) : tableData.length > 0 ? (
                  tableData.map((item, index) => {
                    // 1. Check both possible keys coming from backend
                    const rawCertString =
                      item.Certificate || item.CertificateURL;

                    // 2. Safely transform S3 URIs to HTTPS browser links
                    let certificateUrl = null;
                    if (rawCertString) {
                      if (rawCertString.startsWith("s3://")) {
                        // Converts "s3://bucket-name/folder/file" to "https://bucket-name.s3.amazonaws.com/folder/file"
                        const pathWithoutS3 = rawCertString.replace(
                          "s3://",
                          "",
                        );
                        const parts = pathWithoutS3.split("/");
                        const bucketName = parts.shift();
                        const objectKey = parts.join("/");
                        certificateUrl = `https://${bucketName}.s3.amazonaws.com/${objectKey}`;
                      } else {
                        // If it's already http:// or a local path, keep it as is
                        certificateUrl = rawCertString;
                      }
                    }

                    return (
                      <tr key={item.VehicleNumber || index}>
                        <td>{item.UniqueDeviceId || "N/A"}</td>
                        <td>{item.VehicleNumber || "N/A"}</td>
                        <td>{item.IMEI || "N/A"}</td>
                        <td>{item.VIMNumber || "N/A"}</td>
                        <td>{item.ICCID || "N/A"}</td>
                        <td>
                          {certificateUrl ? (
                            <a
                              href={certificateUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#3B82F6",
                                textDecoration: "underline",
                                textUnderlineOffset: "2px", // Pushes the line slightly down for a cleaner look
                                fontWeight: "600",
                              }}
                            >
                              Link
                            </a>
                          ) : (
                            "N/A"
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan="6"
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      No vehicles found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {tableData.length > 0 && (
            <div
              className="pagination-controls"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "1rem",
                padding: "10px 0",
              }}
            >
              <button
                onClick={() => setPage((old) => Math.max(old - 1, 1))}
                disabled={!paginationInfo.HasPrev}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "8px 12px",
                  border: "1px solid #ccc",
                  borderRadius: "5px",
                  background: paginationInfo.HasPrev ? "#fff" : "#f3f4f6",
                  cursor: paginationInfo.HasPrev ? "pointer" : "not-allowed",
                  color: paginationInfo.HasPrev ? "#333" : "#9ca3af",
                }}
              >
                <ChevronLeft size={16} /> Previous
              </button>

              <span
                style={{ fontSize: "14px", color: "#555", fontWeight: "500" }}
              >
                Page {paginationInfo.Page} of {paginationInfo.TotalPages}
              </span>

              <button
                onClick={() =>
                  setPage((old) => (paginationInfo.HasNext ? old + 1 : old))
                }
                disabled={!paginationInfo.HasNext}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "8px 12px",
                  border: "1px solid #ccc",
                  borderRadius: "5px",
                  background: paginationInfo.HasNext ? "#fff" : "#f3f4f6",
                  cursor: paginationInfo.HasNext ? "pointer" : "not-allowed",
                  color: paginationInfo.HasNext ? "#333" : "#9ca3af",
                }}
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
